import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Users, UserCheck, AlertCircle, FileText, ChevronDown, Check, RefreshCw, Download } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { WorkflowConfigEditor } from './WorkflowConfigEditor';
import { DropdownMenu } from '../ui/DropdownMenu';

interface WorkflowParameter {
  name: string;
  type: string;
  required: boolean;
  default?: any;
  description?: string;
  choices?: string[];
  dynamicSource?: string;
  dynamicPath?: string;
  dependsOn?: string[];
  label?: string;
  visibleWhen?: { param: string; equals: string | string[] };
}

interface EligibleApprover {
  email: string;
  name: string;
}

interface WorkflowConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workflow: {
    name: string;
    displayName: string;
    description: string;
    parameters: WorkflowParameter[];
    annotations?: Record<string, string>;
    configType?: string;
    access: {
      canExecute: boolean;
      requiresApproval: boolean;
      accessLevel: 'unconditional' | 'conditional' | 'none';
    };
  };
  onSubmit: (parameters: Record<string, any>, approvalOptions?: { notifyMode: 'select' | 'all'; selectedApprovers?: string[] }) => void;
  isSubmitting: boolean;
}

type DefaultConfigStatus = 'idle' | 'loading' | 'loaded' | 'error';
// (all four states used — idle/loading/loaded/error)

export function WorkflowConfigDialog({
  isOpen,
  onClose,
  workflow,
  onSubmit,
  isSubmitting,
}: WorkflowConfigDialogProps) {
  const [parameters, setParameters] = useState<Record<string, any>>(() => {
    const defaults: Record<string, any> = {};
    workflow.parameters.forEach((param) => {
      if (param.default !== undefined) {
        defaults[param.name] = param.default;
      }
    });
    return defaults;
  });

  const isConfigurable = workflow.annotations?.['portal.maxiron.cloud/configurable'] === 'true';
  const configType = workflow.configType ?? workflow.annotations?.['portal.maxiron.cloud/config-type'];
  const isTenantAware = isConfigurable && configType === 'tenant-aware';
  const requiresDocker = workflow.annotations?.['portal.maxiron.cloud/requires-docker'] === 'true';

  // YAML editor state — controlled by dialog
  const [yamlContent, setYamlContent] = useState<string>('');
  const [isYamlValid, setIsYamlValid] = useState<boolean>(true);

  // Default config loading state
  const [defaultConfigStatus, setDefaultConfigStatus] = useState<DefaultConfigStatus>('idle');
  const [defaultConfigLoading, setDefaultConfigLoading] = useState(false);
  const [defaultConfigError, setDefaultConfigError] = useState<string | null>(null);
  const [loadedForParams, setLoadedForParams] = useState<string>('');

  // Tenants list state
  const [tenants, setTenants] = useState<Array<{ code: string; name: string }>>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Dynamic options cache
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, string[]>>({});
  const [loadingDynamicOptions, setLoadingDynamicOptions] = useState<Record<string, boolean>>({});

  // Approval state
  const [notifyMode, setNotifyMode] = useState<'all' | 'select'>('all');
  const [eligibleApprovers, setEligibleApprovers] = useState<EligibleApprover[]>([]);
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [approversError, setApproversError] = useState<string | null>(null);

  // Load tenants when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadTenants();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && workflow.access.requiresApproval) {
      loadEligibleApprovers();
    }
  }, [isOpen, workflow.name, workflow.access.requiresApproval]);

  // Reset state when workflow changes
  useEffect(() => {
    const defaults: Record<string, any> = {};
    workflow.parameters.forEach((param) => {
      if (param.default !== undefined) {
        defaults[param.name] = param.default;
      }
    });
    setParameters(defaults);
    setDynamicOptions({});
    setLoadingDynamicOptions({});
    setNotifyMode('all');
    setSelectedApprovers([]);
    setYamlContent('');
    setIsYamlValid(true);
    setDefaultConfigStatus('idle');
    setDefaultConfigLoading(false);
    setDefaultConfigError(null);
    setLoadedForParams('');
  }, [workflow.name]);

  // Load dynamic options whenever isOpen or tenantCode changes
  useEffect(() => {
    if (!isOpen || !parameters.tenantCode) return;
    loadOptionsForTenant(parameters.tenantCode as string, parameters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, parameters.tenantCode, workflow.name]);

  const loadTenants = async () => {
    setLoadingTenants(true);
    try {
      const response = await apiClient.getTenants();
      setTenants(response?.tenants || []);
    } catch {
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadEligibleApprovers = async () => {
    setLoadingApprovers(true);
    setApproversError(null);
    try {
      const approvers = await apiClient.getEligibleApprovers(workflow.name);
      setEligibleApprovers(approvers);
      if (approvers.length === 0) {
        setApproversError('No eligible approvers found. Contact an administrator.');
      }
    } catch {
      setApproversError('Failed to load approvers. Try again.');
    } finally {
      setLoadingApprovers(false);
    }
  };

  /**
   * Normalise well-known params to their correct types regardless of what the backend
   * descriptor says. This makes provider/environment dropdowns work even before the OCI
   * descriptor has been updated to include global_params.
   */
  const normalizeParam = (p: WorkflowParameter): WorkflowParameter => {
    if (p.name === 'provider' && p.type !== 'dynamic-select') {
      return { ...p, type: 'dynamic-select', dynamicSource: 'tenant-providers', dependsOn: ['tenantCode'], required: p.required ?? true, label: p.label || 'Provider' };
    }
    if (p.name === 'environment' && p.type !== 'dynamic-select') {
      return { ...p, type: 'dynamic-select', dynamicSource: 'tenant-environments', dependsOn: ['tenantCode'], required: false, label: p.label || 'Environment (optional)' };
    }
    if (p.name === 'product' && p.type !== 'dynamic-select') {
      return { ...p, type: 'dynamic-select', dynamicSource: 'tenant-devops-products', dependsOn: ['tenantCode'], required: p.required ?? true, label: p.label || 'Product' };
    }
    return p;
  };

  /**
   * Determine if "Load Default Config" should be enabled.
   * All required visible parameters must be filled.
   */
  const alwaysHidden = ['maxironYamlB64', 'configOverrides', 'runId', 'triggeredBy'];
  const visibleParams = workflow.parameters
    .filter((p) => !alwaysHidden.includes(p.name))
    .map(normalizeParam)
    .filter((p) => {
      if (!p.visibleWhen) return true;
      const current = parameters[p.visibleWhen.param];
      const allowed = Array.isArray(p.visibleWhen.equals)
        ? p.visibleWhen.equals
        : [p.visibleWhen.equals];
      return allowed.includes(current as string);
    });
  const requiredParams = visibleParams.filter((p) => p.required !== false);
  const allRequiredFilled = requiredParams.every((p) => {
    const val = parameters[p.name];
    return val !== undefined && val !== null && val !== '';
  });
  const canLoadDefaults = isTenantAware && !!parameters.tenantCode && allRequiredFilled;

  const currentParamsKey = JSON.stringify(
    Object.fromEntries(
      Object.entries(parameters)
        .filter(([k]) => !alwaysHidden.includes(k))
        .sort(([a], [b]) => a.localeCompare(b)),
    ),
  );
  const configIsStale = defaultConfigStatus === 'loaded' && loadedForParams !== currentParamsKey;

  const handleLoadDefaultConfig = async () => {
    setDefaultConfigLoading(true);
    setDefaultConfigError(null);
    try {
      const paramEntries = Object.entries(parameters)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)]);
      const result = await apiClient.getWorkflowDefaultConfig(workflow.name, Object.fromEntries(paramEntries));
      setYamlContent(result.yaml || '');
      setIsYamlValid(true);
      setDefaultConfigStatus('loaded');
      setLoadedForParams(currentParamsKey);
    } catch (err: any) {
      setDefaultConfigError(err?.message || 'Failed to load default configuration');
      setDefaultConfigStatus('error');
    } finally {
      setDefaultConfigLoading(false);
    }
  };

  const handleYamlChange = (newYaml: string, valid: boolean) => {
    setYamlContent(newYaml);
    setIsYamlValid(valid);
  };

  const fetchDynamicOptionsFor = (paramName: string, tenantCode: string, currentParams: Record<string, any>) => {
    const p = visibleParams.find((vp) => vp.name === paramName);
    if (!p || p.type !== 'dynamic-select' || !p.dynamicSource) return;
    setLoadingDynamicOptions((prev) => ({ ...prev, [paramName]: true }));
    const extraParams: Record<string, string> = {};
    if (p.dynamicPath) {
      for (const [k, v] of Object.entries(currentParams)) {
        if (v !== undefined && v !== '') extraParams[k] = String(v);
      }
    }
    apiClient
      .getTenantOptions(tenantCode, p.dynamicSource, p.dynamicPath ? { path: p.dynamicPath, ...extraParams } : {})
      .then((res) => setDynamicOptions((prev) => ({ ...prev, [paramName]: res?.options ?? [] })))
      .catch(() => setDynamicOptions((prev) => ({ ...prev, [paramName]: [] })))
      .finally(() => setLoadingDynamicOptions((prev) => ({ ...prev, [paramName]: false })));
  };

  const loadOptionsForTenant = (tenantCode: string, currentParams: Record<string, any>) => {
    if (!tenantCode) return;
    // Load options for every dynamic-select param that depends on tenantCode
    visibleParams
      .filter((p) => p.type === 'dynamic-select' && p.dynamicSource && p.dependsOn?.includes('tenantCode'))
      .forEach((p) => fetchDynamicOptionsFor(p.name, tenantCode, currentParams));
  };

  const handleChange = (name: string, value: any) => {
    const next = { ...parameters, [name]: value };
    setParameters(next);

    if (name === 'tenantCode') {
      // Tenant changed: clear dependent options then reload them
      setDynamicOptions({});
      if (value) loadOptionsForTenant(value as string, next);
    } else {
      // Another param changed: reload options for params that depend on it
      const dependentNames = visibleParams
        .filter((p) => p.type === 'dynamic-select' && p.dependsOn?.includes(name))
        .map((p) => p.name);
      const tenantCode = next.tenantCode as string;
      if (tenantCode) {
        dependentNames.forEach((pName) => fetchDynamicOptionsFor(pName, tenantCode, next));
      }
    }

    // Reset loaded config when params change
    if (defaultConfigStatus === 'loaded') {
      setDefaultConfigStatus('idle');
      setYamlContent('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isConfigurable && !isYamlValid) {
      setApproversError('Please fix YAML configuration errors before submitting.');
      return;
    }
    const finalParameters = isConfigurable
      ? { ...parameters, configOverrides: yamlContent }
      : parameters;
    if (workflow.access.requiresApproval) {
      if (notifyMode === 'select' && selectedApprovers.length === 0) {
        setApproversError('Please select at least one approver.');
        return;
      }
      onSubmit(finalParameters, {
        notifyMode,
        selectedApprovers: notifyMode === 'select' ? selectedApprovers : undefined,
      });
    } else {
      onSubmit(finalParameters);
    }
  };

  const toggleApprover = (email: string) => {
    setSelectedApprovers((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
    setApproversError(null);
  };

  const renderParam = (param: WorkflowParameter) => {
    if (param.name === 'tenantCode') {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || 'Tenant'}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={parameters[param.name] || ''}
              onChange={(e) => handleChange(param.name, e.target.value)}
              className="hidden md:block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={param.required !== false}
              disabled={loadingTenants}
            >
              <option value="">{loadingTenants ? 'Loading tenants...' : 'Select a tenant...'}</option>
              {tenants.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name || t.code}
                </option>
              ))}
            </select>
            <div className="md:hidden w-full">
              <DropdownMenu
                className="w-full"
                align="left"
                trigger={
                  <button
                    type="button"
                    disabled={loadingTenants}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] flex items-center justify-between"
                  >
                    <span className="min-w-0 truncate">
                      {parameters[param.name]
                        ? tenants.find((t) => t.code === parameters[param.name])?.name || parameters[param.name]
                        : 'Select a tenant...'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </button>
                }
                items={[
                  {
                    label: 'Select a tenant...',
                    icon: !parameters[param.name] ? <Check className="w-4 h-4" /> : undefined,
                    onClick: () => handleChange(param.name, ''),
                  },
                  ...tenants.map((t) => ({
                    label: t.name || t.code,
                    icon: t.code === parameters[param.name] ? <Check className="w-4 h-4" /> : undefined,
                    onClick: () => handleChange(param.name, t.code),
                  })),
                ]}
              />
            </div>
          </div>
        </div>
      );
    }

    if (param.type === 'dynamic-select') {
      const rawChoices = dynamicOptions[param.name] ?? [];
      const disabled = loadingDynamicOptions[param.name] || param.dependsOn?.some((dep) => !parameters[dep]);
      const placeholder = loadingDynamicOptions[param.name]
        ? 'Loading...'
        : param.dependsOn?.some((dep) => !parameters[dep])
          ? `Select ${param.dependsOn?.join(', ')} first`
          : `Select ${param.label || param.name}...`;
      const selectOptions = rawChoices.length > 0 ? rawChoices : (!param.required ? [''] : []);
      const displayLabel = (val: string) => {
        if (val === '') return '(None)';
        return ['provider', 'environment'].includes(param.name) ? val.toUpperCase() : val;
      };

      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          {param.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{param.description}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={parameters[param.name] ?? ''}
              onChange={(e) => handleChange(param.name, e.target.value)}
              className="hidden md:block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              required={param.required !== false}
              disabled={disabled}
            >
              <option value="">{placeholder}</option>
              {selectOptions.map((opt) => (
                <option key={opt || '__none__'} value={opt}>
                  {displayLabel(opt)}
                </option>
              ))}
            </select>
            <div className="md:hidden w-full">
              <DropdownMenu
                className="w-full"
                align="left"
                trigger={
                  <button
                    type="button"
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[44px] flex items-center justify-between disabled:opacity-50"
                  >
                    <span className="min-w-0 truncate">
                      {parameters[param.name] !== undefined && parameters[param.name] !== ''
                        ? displayLabel(parameters[param.name])
                        : placeholder}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </button>
                }
                items={[
                  {
                    label: placeholder,
                    icon: !parameters[param.name] ? <Check className="w-4 h-4" /> : undefined,
                    onClick: () => handleChange(param.name, ''),
                  },
                  ...selectOptions.map((opt) => ({
                    label: displayLabel(opt),
                    icon: opt === parameters[param.name] ? <Check className="w-4 h-4" /> : undefined,
                    onClick: () => handleChange(param.name, opt),
                  })),
                ]}
              />
            </div>
          </div>
        </div>
      );
    }

    if (param.type === 'boolean') {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
          </label>
          <select
            value={parameters[param.name] !== undefined ? String(parameters[param.name]) : 'false'}
            onChange={(e) => handleChange(param.name, e.target.value === 'true')}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="false">False</option>
            <option value="true">True</option>
          </select>
        </div>
      );
    }

    if (param.type === 'choice' && param.choices) {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            value={parameters[param.name] || ''}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100"
            required={param.required !== false}
          >
            <option value="">-- Select --</option>
            {param.choices.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      );
    }

    if (param.type === 'number') {
      return (
        <div key={param.name} className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {param.label || param.name}
            {param.required !== false && <span className="text-red-500 ml-1">*</span>}
          </label>
          <Input
            type="number"
            value={parameters[param.name] ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
              handleChange(param.name, v);
            }}
            placeholder={param.default !== undefined ? `Default: ${param.default}` : ''}
            required={param.required !== false}
          />
        </div>
      );
    }

    // string / fallback
    return (
      <div key={param.name} className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {param.label || param.name}
          {param.required !== false && <span className="text-red-500 ml-1">*</span>}
        </label>
        {param.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400">{param.description}</p>
        )}
        <Input
          type="text"
          value={parameters[param.name] || ''}
          onChange={(e) => handleChange(param.name, e.target.value)}
          placeholder={param.default !== undefined ? `Default: ${param.default}` : ''}
          required={param.required !== false}
        />
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent maxWidth="3xl">
        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
          <DialogHeader>
            <DialogTitle>{workflow.displayName}</DialogTitle>
          </DialogHeader>

          <DialogBody className="max-h-[75vh]">
            <div className="space-y-5">
              {workflow.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{workflow.description}</p>
              )}

              {/* ── Step 1: Parameters ─────────────────────────────────────── */}
              {visibleParams.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Parameters
                    </span>
                    {requiresDocker && (
                      <Badge variant="outline" className="text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-600">
                        Docker Required
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-4">
                    {visibleParams.map((param) => renderParam(param))}
                  </div>
                </div>
              )}

              {/* ── Step 2: Configuration (tenant-aware workflows) ──────────── */}
              {isTenantAware && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Configuration
                    </span>
                    {defaultConfigStatus === 'loaded' && !configIsStale && (
                      <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Loaded
                      </span>
                    )}
                    {configIsStale && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">
                        Params changed — reload needed
                      </span>
                    )}
                  </div>

                  {/* Load button panel */}
                  {(defaultConfigStatus === 'idle' || configIsStale || defaultConfigStatus === 'error') && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {configIsStale
                              ? 'Parameters changed — reload default configuration'
                              : 'Load the default configuration for the selected tenant and provider'}
                          </p>
                          {!parameters.tenantCode && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Select a tenant above to enable this step.
                            </p>
                          )}
                          {parameters.tenantCode && !allRequiredFilled && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              Fill all required parameters to load defaults.
                            </p>
                          )}
                          {defaultConfigStatus === 'error' && defaultConfigError && (
                            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{defaultConfigError}</p>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={handleLoadDefaultConfig}
                        disabled={!canLoadDefaults || defaultConfigLoading}
                        className="w-full sm:w-auto gap-2"
                      >
                        {defaultConfigLoading ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            {configIsStale ? 'Reload Default Configuration' : 'Load Default Configuration'}
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* YAML editor — shown once config is loaded */}
                  {defaultConfigStatus === 'loaded' && !configIsStale && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Defaults loaded for{' '}
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {parameters.tenantCode}
                            {parameters.provider ? ` / ${parameters.provider}` : ''}
                          </span>
                          . Edit if needed before executing.
                        </p>
                        <button
                          type="button"
                          onClick={handleLoadDefaultConfig}
                          disabled={defaultConfigLoading}
                          className="text-xs flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 transition-colors whitespace-nowrap"
                        >
                          <RefreshCw className={`w-3 h-3 ${defaultConfigLoading ? 'animate-spin' : ''}`} />
                          Reload
                        </button>
                      </div>
                      <WorkflowConfigEditor
                        value={yamlContent}
                        onChange={handleYamlChange}
                        isLoading={defaultConfigLoading}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* ── Configuration for manual/non-tenant-aware configurable workflows ── */}
              {isConfigurable && !isTenantAware && (
                <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-5 space-y-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    Configuration (YAML)
                  </span>
                  <WorkflowConfigEditor
                    value={yamlContent || '# Add your workflow configuration here\n'}
                    onChange={handleYamlChange}
                  />
                </div>
              )}

              {/* ── Approval ─────────────────────────────────────────────────── */}
              {workflow.access.requiresApproval && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                  <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Approval Required</p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                          This workflow requires approval before execution. Choose how to notify approvers.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Users className="w-4 h-4 inline mr-2" />
                      Notification Method
                    </label>
                    <div className="flex gap-4">
                      {(['all', 'select'] as const).map((mode) => (
                        <label key={mode} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="notifyMode"
                            value={mode}
                            checked={notifyMode === mode}
                            onChange={() => setNotifyMode(mode)}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {mode === 'all'
                              ? `Notify all approvers (${eligibleApprovers.length})`
                              : 'Select specific approvers'}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {notifyMode === 'select' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          <UserCheck className="w-4 h-4 inline mr-2" />
                          Select Approvers
                          {selectedApprovers.length > 0 && (
                            <Badge variant="outline" className="ml-2">{selectedApprovers.length} selected</Badge>
                          )}
                        </label>
                        <div className="flex gap-2">
                          <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedApprovers(eligibleApprovers.map((a) => a.email))} disabled={eligibleApprovers.length === 0}>
                            Select All
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedApprovers([])} disabled={selectedApprovers.length === 0}>
                            Clear
                          </Button>
                        </div>
                      </div>
                      {loadingApprovers ? (
                        <div className="text-sm text-gray-500 py-4 text-center">Loading approvers...</div>
                      ) : eligibleApprovers.length === 0 ? (
                        <div className="text-sm text-red-600 dark:text-red-400 py-4 text-center">No eligible approvers found</div>
                      ) : (
                        <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
                          {eligibleApprovers.map((approver) => (
                            <label key={approver.email} className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedApprovers.includes(approver.email)}
                                onChange={() => toggleApprover(approver.email)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{approver.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{approver.email}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {approversError && (
                    <div className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {approversError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                (isConfigurable && !isYamlValid) ||
                (isTenantAware && defaultConfigStatus !== 'loaded') ||
                (workflow.access.requiresApproval && eligibleApprovers.length === 0)
              }
            >
              {isSubmitting ? 'Submitting...' : 'Execute Now'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
